import { hasSystemAdminAccess, useAuth } from '@/contexts/AuthContext';
import { loginUrlWithNext } from '@/lib/safeNextPath';
import type { ReactNode } from 'react';
import { useLocation } from 'wouter';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requiredPermissions?: string[];
}

export function ProtectedRoute({ children, requireAdmin = false, requiredPermissions }: ProtectedRouteProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-primary border-b-2" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    setLocation(loginUrlWithNext(`${window.location.pathname}${window.location.search}`));
    return null;
  }

  // Check admin access if required
  if (requireAdmin && !hasSystemAdminAccess(user)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="space-y-4 text-center">
          <h1 className="font-bold text-2xl text-foreground">Aðgangur bannaður</h1>
          <p className="text-muted-foreground">Þú hefur ekki réttindi til að skoða þessa síðu.</p>
          <button type="button" onClick={() => setLocation('/')} className="text-primary hover:underline">
            Fara aftur á mælaborð
          </button>
        </div>
      </div>
    );
  }

  // Check required permissions if specified. The user passes if they hold ANY of
  // the required permissions; accounts:admin always subsumes (via hasSystemAdminAccess).
  if (requiredPermissions && requiredPermissions.length > 0) {
    const userPermissions = user?.permissions ?? [];
    const hasRequiredPermission =
      hasSystemAdminAccess(user) || requiredPermissions.some((permission) => userPermissions.includes(permission));
    if (!hasRequiredPermission) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <div className="space-y-4 text-center">
            <h1 className="font-bold text-2xl text-foreground">Aðgangur bannaður</h1>
            <p className="text-muted-foreground">Þú hefur ekki réttindi til að skoða þessa síðu.</p>
            <button type="button" onClick={() => setLocation('/')} className="text-primary hover:underline">
              Fara aftur á mælaborð
            </button>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
